import { InfoIcon } from "../../assets/icons/infoIcon";
import { ViewPasswordIcon } from "../../assets/icons/viewPasswordIcon";
import {
  StyledErrorMessageWrapper,
  StyledInfoWrapper,
  StyledInputBox,
  StyledInputContainer,
  StyledInputContentWrapper,
  StyledLabel,
  StyledLabelInfoGroup,
  StyledTextInput,
  StyledPasswordIconWrapper,
} from "./FormFieldStyles";
import { Tooltip } from "./Tooltip";
import { useId, useRef, useState } from "react";
import { Text } from "../../utils/text";
import { ErrorIcon } from "../../assets/icons/errorIcon";
import { HidePasswordIcon } from "../../assets/icons/hidePasswordIcon";
import { RequiredIndicator } from "./Form";
import { designSystemVars as vars } from "../tokens";

interface TextInputProps {
  isRequired?: boolean;
  label?: string;
  type?: string;
  inputValue?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tooltip?: string | null;
  tooltipTriggerTabIndex?: 0 | -1;
  showError?: string;
  errorMessage?: string;
  error?: string;
  $disabled?: boolean;
  $bgColor?: string;
  width?: string;
  customInput?: React.ReactNode;
  name?: string;
  id?: string;
  disableError?: boolean;
  validateRequiredOnBlur?: boolean;
  disabled?: boolean;
  containerPadding?: string;
  containerMargin?: string;
}

export const TextInputField: React.FC<TextInputProps> = ({
  error,
  isRequired = true,
  label,
  type = "text",
  inputValue,
  placeholder,
  onChange,
  tooltip = "",
  tooltipTriggerTabIndex,
  showError,
  errorMessage,
  $disabled = false,
  width,
  customInput,
  name,
  id,
  disableError = false,
  validateRequiredOnBlur = true,
  disabled = false,
  containerPadding,
  containerMargin,
}) => {
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseDownRef = useRef(false);

  const isPasswordInput = type === "password";
  const generatedId = useId();
  const controlId = id || generatedId;
  const errorId = `${controlId}-error`;
  const visuallyDisabled = $disabled || disabled;

  const inputType = isPasswordInput
    ? showPassword
      ? "text"
      : "password"
    : type;

  const handleFocus = () => {
    setIsKeyboardFocus(!mouseDownRef.current);
    mouseDownRef.current = false;

    if (!inputRef.current) return;

    const currentType = inputRef.current.type;

    if (["text", "password", "search", "tel", "url"].includes(currentType)) {
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  };

  // const showValidationError =
  //   !disableError && (showError || (touched && isRequired && !inputValue));
  const showValidationError =
    !disableError &&
    (error ||
      showError ||
      (validateRequiredOnBlur && touched && isRequired && !inputValue));
  const validationMessage =
    error ||
    errorMessage ||
    showError ||
    (label ? `${label} is required` : "This field is required");

  return (
    <StyledInputBox>
      <StyledInputContainer
        // haveError={showValidationError ? "true" : undefined}
        haveError={!!showValidationError}
        $disabled={visuallyDisabled}
        $focusVisible={isKeyboardFocus}
        $padding={containerPadding}
        $margin={containerMargin}
        ref={containerRef}
        onClick={(event) => {
          if (
            tooltipTriggerTabIndex !== undefined &&
            (event.target as Element).closest("[data-tooltip-trigger]")
          )
            return;
          const active = document.activeElement;
          const alreadyFocused =
            active instanceof HTMLElement &&
            ["INPUT", "SELECT", "TEXTAREA", "BUTTON"].includes(active.tagName);
          if (alreadyFocused) return;

          if (inputRef.current) {
            inputRef.current.focus();
            return;
          }

          containerRef.current
            ?.querySelector<HTMLInputElement>("input")
            ?.focus();
        }}
        onMouseDown={() => {
          mouseDownRef.current = true;
        }}
      >
        <StyledInputContentWrapper $disabled={visuallyDisabled}>
          <div
            style={{ order: 2, width: "100%", alignSelf: "stretch" }}
            onFocus={handleFocus}
            onBlur={() => {
              setTouched(true);
              setIsKeyboardFocus(false);
            }}
          >
            {customInput ? (
              customInput
            ) : (
              <>
                <StyledTextInput
                  ref={inputRef}
                  id={controlId}
                  type={inputType}
                  value={inputValue}
                  placeholder={placeholder}
                  onChange={onChange}
                  aria-invalid={!!showValidationError}
                  aria-required={isRequired}
                  aria-describedby={showValidationError ? errorId : undefined}
                  $disabled={visuallyDisabled}
                  name={name}
                  $hasPasswordIcon={isPasswordInput}
                  disabled={disabled}
                />

                {isPasswordInput && (
                  <StyledPasswordIconWrapper
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPassword((prev) => !prev);
                      inputRef.current?.focus();
                    }}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <HidePasswordIcon aria-hidden="true" />
                    ) : (
                      <ViewPasswordIcon aria-hidden="true" />
                    )}
                  </StyledPasswordIconWrapper>
                )}
              </>
            )}
          </div>

          {label && (
            <StyledLabelInfoGroup style={{ order: 1 }}>
              <StyledLabel
                // haveError={showValidationError ? "true" : undefined}
                haveError={!!showValidationError}
                htmlFor={customInput ? undefined : controlId}
              >
                {label} {isRequired && <RequiredIndicator>*</RequiredIndicator>}
              </StyledLabel>

              {tooltip && (
                <Tooltip
                  tooltip={tooltip}
                  triggerTabIndex={tooltipTriggerTabIndex}
                  triggerAriaLabel={
                    tooltipTriggerTabIndex !== undefined
                      ? `${label} information`
                      : undefined
                  }
                  fullWidth={tooltipTriggerTabIndex === undefined}
                >
                  <StyledInfoWrapper aria-hidden="true">
                    <InfoIcon aria-hidden="true" />
                  </StyledInfoWrapper>
                </Tooltip>
              )}
            </StyledLabelInfoGroup>
          )}
        </StyledInputContentWrapper>
      </StyledInputContainer>
      {showValidationError && (
        <StyledErrorMessageWrapper id={errorId} role="alert">
          <ErrorIcon aria-hidden="true" />
          <Text
            fontFamily={vars.typography.family.sans}
            fontSize="var(--inzeloop-ds-font-size-xs)"
            fontStyle="normal"
            fontWeight="var(--inzeloop-ds-font-weight-regular)"
            $lineHeight="var(--inzeloop-ds-line-height-sm)"
            color={vars.color.control.errorText}
          >
            {validationMessage}
          </Text>
        </StyledErrorMessageWrapper>
      )}
    </StyledInputBox>
  );
};
